import React, { useState, useRef } from "react";
import { toast } from "sonner";
import { Camera, Loader2, Upload, X } from "lucide-react";
import api from "../lib/api";

/**
 * Compact "Share palm photo" trigger button + modal uploader.
 * Posts the photo as multipart to /api/chats/:id/palm-photo and waits for the
 * AI annotated reply. On success it calls onUploaded() so ChatPage refetches
 * the message timeline.
 */
const PalmUploader = ({ chatId, onUploaded, question, billingNotStarted = false }) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleOpen = () => {
    if (billingNotStarted) {
      toast.info(
        "Please send your first message — your chat session will begin and then you can share your palm for analysis.",
        { duration: 5000 },
      );
      return;
    }
    setOpen(true);
  };

  const onPick = (f) => {
    if (!f) return;
    if (!/^image\/(jpe?g|png|webp|heic|heif)$/i.test(f.type)) {
      toast.error("Please upload a JPG, PNG or WEBP image.");
      return;
    }
    if (f.size > 12 * 1024 * 1024) {
      toast.error("Photo too large (max 12 MB).");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleSubmit = async () => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("photo", file);
    if (question) fd.append("question", question);
    try {
      // Use the shared axios instance — it carries the session cookie auth.
      const { data } = await api.post(`/chats/${chatId}/palm-photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,  // Claude vision can take up to ~60s
      });
      toast.success("Palm read complete — see analysis below");
      onUploaded && onUploaded(data);
      setOpen(false);
      setFile(null);
      setPreview(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "unknown error";
      toast.error(`Palm upload failed: ${msg}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className={`flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-[#FFEDC4] border border-[#D4AF37]/70 text-[#7A5A12] text-xs font-medium hover:bg-[#FFE19D] transition-colors ${billingNotStarted ? "opacity-60" : ""}`}
        data-testid="palm-share-btn"
        title={billingNotStarted
          ? "Send your first message — your session will start, then you can share your palm"
          : "Share a palm photo for hasta rekha reading"}
      >
        <Camera size={13} />
        <span className="hidden sm:inline">Share palm photo</span>
        <span className="sm:hidden">Palm</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-3"
          onClick={() => !uploading && setOpen(false)}
          data-testid="palm-uploader-modal"
        >
          <div
            className="w-full max-w-sm bg-bg rounded-t-2xl sm:rounded-2xl border border-soft shadow-xl p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-serif-display text-lg text-foreground">Share your palm</h3>
              <button
                type="button"
                onClick={() => !uploading && setOpen(false)}
                className="text-muted hover:text-foreground p-1"
                data-testid="palm-close-btn"
              >
                <X size={16} />
              </button>
            </div>
            <p className="text-xs text-muted mb-3 leading-relaxed">
              Photograph your <b>dominant</b> palm (the one you write with). Fingers naturally spread,
              palm facing the camera, lit by daylight or a soft white lamp. <b>No flash</b>, no rings.
            </p>

            {!preview ? (
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="w-full h-40 border-2 border-dashed border-[#D4AF37]/70 rounded-xl bg-[#FFF9E6] flex flex-col items-center justify-center text-[#7A5A12] hover:bg-[#FFEDC4]/60 transition-colors"
                data-testid="palm-pick-btn"
              >
                <Upload size={22} className="mb-2" />
                <span className="text-sm font-medium">Tap to pick a photo</span>
                <span className="text-[10px] mt-1 text-muted">JPG / PNG / WEBP · max 12 MB</span>
              </button>
            ) : (
              <div className="relative">
                <img
                  src={preview}
                  alt="palm preview"
                  className="w-full max-h-72 object-contain rounded-xl border border-soft bg-black/5"
                  data-testid="palm-preview"
                />
                <button
                  type="button"
                  onClick={() => { setFile(null); setPreview(null); }}
                  className="absolute top-2 right-2 bg-black/70 text-white rounded-full p-1.5"
                  data-testid="palm-clear-btn"
                  disabled={uploading}
                >
                  <X size={14} />
                </button>
              </div>
            )}

            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
              className="hidden"
              onChange={(e) => onPick(e.target.files?.[0])}
              data-testid="palm-input"
            />

            {preview && (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={uploading}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#1A1410] font-medium px-4 py-2 rounded-full hover:bg-[#E8C77A] disabled:opacity-60 transition-colors"
                data-testid="palm-submit-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Reading your palm…
                  </>
                ) : (
                  <>
                    <Camera size={14} />
                    Send for hasta rekha reading
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default PalmUploader;
